import { ChannelType, ConversationClassificationStatus, ConversationCloseReason, ConversationPriority, ConversationStatus, CONVERSATION_STATUS_LABELS, isConversationClosedForMessaging, SupportMessageType } from './atendimentoEnums';
import { SupportMessage } from './supportMessage';
import { Label } from './label';

export interface ClassificationBadge {
  fieldCode: string;
  fieldTitle: string;
  slug: string;
  label: string;
  color?: string | null;
}

export interface ConversationLastMessagePreview {
  id: string;
  messageType: SupportMessageType;
  isPrivate: boolean;
  preview?: string | null;
  createdAt: string;
}

export interface Conversation {
  id: string;
  inboxId: string;
  inboxName: string;
  channelType: ChannelType;
  enterSendsMessage?: boolean;
  contactId: string;
  contactName: string;
  contactEmail?: string | null;
  contactPhone?: string | null;
  contactIdentifier?: string | null;
  contactInboxId?: string | null;
  assigneeId?: string | null;
  assigneeName?: string | null;
  teamId?: string | null;
  teamName?: string | null;
  supportGroupId?: string | null;
  supportGroupName?: string | null;
  parentConversationId?: string | null;
  displayId: number;
  uuid: string;
  status: ConversationStatus;
  priority?: ConversationPriority | null;
  lastActivityAt: string;
  agentLastSeenAt?: string | null;
  assigneeLastSeenAt?: string | null;
  unreadCount?: number;
  lastNonActivityMessage?: ConversationLastMessagePreview | null;
  firstReplyAt?: string | null;
  firstBotReplyAt?: string | null;
  escalatedAt?: string | null;
  assumedAt?: string | null;
  closedAt?: string | null;
  closeReason?: ConversationCloseReason | null;
  classificationResult?: string | null;
  classificationBadges?: ClassificationBadge[];
  classificationStatus?: ConversationClassificationStatus | null;
  classificationAttemptCount?: number;
  classificationNotifiedAt?: string | null;
  classificationCompletedAt?: string | null;
  waitingSince?: string | null;
  snoozedUntil?: string | null;
  customAttributes?: string | null;
  createdAt: string;
  updatedAt: string;
  messages: SupportMessage[];
  labels?: Label[];
}

export interface ConversationFilters {
  inboxId?: string;
  status?: ConversationStatus;
  statuses?: ConversationStatus[];
  assigneeId?: string;
  priority?: ConversationPriority;
  supportGroupId?: string;
  labelId?: string;
  channelType?: ChannelType;
  teamId?: string;
  search?: string;
}

export interface UpdateConversationStatusRequest {
  status: ConversationStatus;
  snoozedUntil?: string | null;
  reason?: string | null;
}

export interface AssignConversationRequest {
  assigneeId?: string | null;
  teamId?: string | null;
}

export interface HandoffToHumanRequest {
  reason?: string | null;
  supportGroupId?: string | null;
}

export interface HandoffToBotRequest {
  reason?: string | null;
}

export type ConversationConductor = 'bot' | 'human' | 'waiting';

export function getConversationConductor(
  conversation: Conversation,
  inboxHasActiveBot = false,
): ConversationConductor {
  if (conversation.assigneeId) {
    return 'human';
  }

  if (conversation.status === ConversationStatus.WaitingForOperator) {
    return 'waiting';
  }

  if (conversation.status === ConversationStatus.InProgress) {
    return 'human';
  }

  if (inboxHasActiveBot) {
    return 'bot';
  }

  return 'waiting';
}

export const CONVERSATION_CONDUCTOR_LABELS: Record<ConversationConductor, string> = {
  bot: 'IA conduzindo',
  human: 'Humano conduzindo',
  waiting: CONVERSATION_STATUS_LABELS[ConversationStatus.WaitingForOperator],
};

export const CONVERSATION_CONDUCTOR_COLORS: Record<ConversationConductor, string> = {
  bot: 'bg-purple-100 text-purple-800',
  human: 'bg-blue-100 text-blue-800',
  waiting: 'bg-orange-100 text-orange-800',
};

export type AssumeHintReason = 'waiting' | 'bot' | 'otherAgent';

export interface AssumeHintContext {
  conversation: Conversation;
  currentUserId?: string | null;
  draft: string;
  dismissed?: boolean;
  inboxHasActiveBot?: boolean;
}

const sameUserId = (left?: string | null, right?: string | null): boolean => {
  if (!left || !right)
  {
    return false;
  }

  return left.toLowerCase() === right.toLowerCase();
};

export function getAssumeHintReason(context: AssumeHintContext): AssumeHintReason | null {
  const { conversation, currentUserId, draft, dismissed } = context;

  if (dismissed || !draft.trim() || !currentUserId)
  {
    return null;
  }

  if (isConversationClosedForMessaging(conversation.status))
  {
    return null;
  }

  if (conversation.assigneeId && sameUserId(conversation.assigneeId, currentUserId))
  {
    return null;
  }

  if (conversation.assigneeId && !sameUserId(conversation.assigneeId, currentUserId))
  {
    return 'otherAgent';
  }

  const conductor = getConversationConductor(conversation, context.inboxHasActiveBot);
  if (conductor === 'waiting')
  {
    return 'waiting';
  }

  if (conductor === 'bot')
  {
    return 'bot';
  }

  return null;
}

export function shouldShowAssumeHint(context: AssumeHintContext): boolean {
  return getAssumeHintReason(context) != null;
}

export function getAssumeHintMessage(
  reason: AssumeHintReason,
  assigneeName?: string | null,
): string {
  if (reason === 'waiting')
  {
    return 'Esta conversa ainda não tem responsável.';
  }

  if (reason === 'bot')
  {
    return 'Esta conversa está sendo conduzida pelo bot.';
  }

  if (assigneeName?.trim())
  {
    return `Esta conversa está atribuída a ${assigneeName.trim()}.`;
  }

  return 'Esta conversa está atribuída a outro operador.';
}
